<template>
  <div class="voucher-wrapper">
    <!-- 针式打印凭证 -->
    <div class="voucher-container" ref="voucherRef" v-loading="loading">
      <div class="voucher-header">
        <h1 class="title">优启学素养中心 业务凭证</h1>
      </div>
      
      <div v-if="voucherData" class="voucher-info">
        <div class="info-row">
          <span>学员姓名：{{ voucherData.studentName }}</span>
          <span>订单类型：{{ voucherData.orderType }}</span>
          <span>经办日期：{{ voucherData.processDate }}</span>
          <span class="order-number">订单号：{{ voucherData.orderNo }}</span>
        </div>
      </div>

      <table class="voucher-table">
        <thead>
          <tr>
            <th>班级信息</th>
            <th>上课时间</th>
            <th>价格</th>
            <th>数量</th>
            <th>原价</th>
            <th>优惠</th>
            <th>应收小计</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in voucherData?.items || []" :key="index">
            <td>
              {{ item.className }}<br>
              教师：{{ item.teacherName }}<br>
              教室：{{ item.classroom }}
            </td>
            <td>{{ item.courseTime }}</td>
            <td>¥ {{ item.unitPrice }}</td>
            <td>{{ item.quantity }}</td>
            <td>¥ {{ item.originalPrice }}</td>
            <td>¥ {{ item.discount }}</td>
            <td>¥ {{ item.totalAmount }}</td>
          </tr>
        </tbody>
      </table>

      <div class="voucher-remarks">
        <span>备注：{{ voucherData?.remarks || '-' }}</span>
      </div>

      <div class="voucher-summary">
        <div class="summary-row">
          <span>应收总计：¥ {{ voucherData?.totalAmount || 0 }}</span>
          <span v-if="voucherData?.transferCredit">转课抵扣：¥ {{ voucherData.transferCredit }}</span>
          <span>实收总计：¥ {{ voucherData?.actualAmount || 0 }}</span>
          <span>欠费：¥ {{ voucherData?.debt || 0 }}</span>
        </div>
      </div>

      <div class="voucher-footer">
        <div class="footer-row">
          <span>电话：029-81870218</span>
          <span>地址：万科金域国际</span>
          <span>经办人：{{ voucherData?.handler?.username || '-' }}</span>
        </div>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="voucher-actions">
      <el-button @click="handlePrint">针式打印</el-button>
      <el-button @click="showThermal = true">热敏打印</el-button>
      <el-button type="primary" @click="exportPDF">导出PDF</el-button>
      <el-button v-if="studentId" type="success" @click="goToStudentDetail">查看学员</el-button>
    </div>

    <!-- 热敏打印预览对话框 -->
    <el-dialog
      v-model="showThermal"
      title="热敏打印预览"
      width="300px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      custom-class="thermal-dialog"
    >
      <div class="thermal-voucher" ref="thermalVoucherRef">
        <div class="header">
          <h1>报名收费凭证</h1>
          <div class="order-info">
            <div>订单号：{{ voucherData?.orderNo }}</div>
            <div>日期：{{ voucherData?.processDate }}</div>
          </div>
        </div>

        <div class="divider">--------------------------------</div>

        <div class="content">
          <div class="student-info">
            <div>学员：{{ voucherData?.studentName }}</div>
            <template v-for="(item, index) in voucherData?.items || []" :key="index">
              <div class="class-info">
                <div>班级：{{ item.className }}</div>
                <div>教师：{{ item.teacherName }}</div>
                <div>教室：{{ item.classroom }}</div>
                <div>上课时间：{{ item.courseTime }}</div>
                <div>报名课时：{{ item.quantity }}课时</div>
                <div class="divider">--------------------------------</div>
              </div>
            </template>
          </div>

        </div>

        <div class="divider">--------------------------------</div>

        <div class="blessing-spacer"></div>

        <div class="blessing">
          <div class="blessing-deco-top">================================</div>
          <div>感 谢 您 的 信 任 与 支 持</div>
          <div>祝 学 习 进 步 ！</div>
          <div class="blessing-deco-bottom">================================</div>
          <div class="blessing-stars">※ ※ ※ ※ ※ ※ ※ ※ ※ ※ ※ ※ ※ ※</div>
          <div class="blessing-tear">- - - - - 请沿此线撕下 - - - - -</div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button type="primary" @click="handleThermalPrint">
            打印
          </el-button>
          <el-button @click="showThermal = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { getOrderById } from '@/api/order'
import { ElMessage } from 'element-plus'

const props = defineProps({
  orderIds: {
    type: Array,
    required: true,
    default: () => []
  },
  studentId: {
    type: String,
    default: ''
  }
})

const router = useRouter()
const loading = ref(false)
const voucherRef = ref(null)
const thermalVoucherRef = ref(null)
const voucherData = ref(null)
const showThermal = ref(false)

// 获取订单数据
const fetchOrderData = async () => {
  loading.value = true
  try {
    // 获取所有订单的数据
    const orderPromises = props.orderIds.map(id => getOrderById(id))
    const responses = await Promise.all(orderPromises)
    console.log('responses',responses)
    
    // 处理第一个订单的基本信息（作为主订单）
    const mainOrder = responses[0].data
    
    // 收集所有班级信息
    const items = responses.map(response => {
      const orderData = response.data
      return {
        className: orderData.class?.course?.name || orderData.class?.name || '-',
        teacherName: orderData.class?.teacher?.name || '-',
        classroom: orderData.class?.classroom ? 
          `${orderData.class.classroom.name || ''}`.trim() : 
          '-',
        courseTime: `${orderData.class?.class_day || ''} ${orderData.class?.time_slot || ''}`.trim(),
        unitPrice: orderData.unitPrice || 0,
        quantity: orderData.quantity || 0,
        originalPrice: orderData.originalPrice || 0,
        discount: orderData.discount || 0,
        totalAmount: orderData.totalAmount || 0
      }
    })

    // 计算总金额
    const totalAmount = responses.reduce((sum, response) => sum + (response.data.totalAmount || 0), 0)
    const actualAmount = responses.reduce((sum, response) => sum + (response.data.actualAmount || 0), 0)
    const transferCredit = responses.reduce((sum, response) => sum + (response.data.transferCredit || 0), 0)
    const debt = responses.reduce((sum, response) => sum + (response.data.debt || 0), 0)

    voucherData.value = {
      studentName: mainOrder.student?.name || '-',
      orderType: mainOrder.orderType || '-',
      processDate: new Date(mainOrder.createdAt).toLocaleDateString(),
      orderNo: mainOrder.orderNo || '-',
      items,
      totalAmount,
      actualAmount,
      transferCredit,
      debt,
      handler: mainOrder.handler || {},
      remarks: mainOrder.remark || ''
    }
  } catch (error) {
    console.error('获取订单数据失败:', error)
    ElMessage.error('获取订单数据失败')
  } finally {
    loading.value = false
  }
}

// 针式打印
const handlePrint = () => {
  window.print()
}

// 热敏打印
const handleThermalPrint = () => {
  const style = document.createElement('style')
  style.textContent = `
    @media print {
      @page {
        margin: 0;
      }
      body * {
        visibility: hidden;
      }
      .thermal-voucher, .thermal-voucher * {
        visibility: visible;
      }
      .thermal-voucher {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        color: #000 !important;
      }
      .thermal-voucher * {
        color: #000 !important;
      }
    }
  `
  document.head.appendChild(style)
  ElMessage.info('请在打印对话框中设置纸张宽度为 58mm，边距设为"无"')
  window.print()
  document.head.removeChild(style)
}

// 导出PDF方法
const exportPDF = async () => {
  try {
    const element = voucherRef.value
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false
    })
    
    const imgData = canvas.toDataURL('image/jpeg', 1.0)
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [canvas.width, canvas.height]
    })
    
    pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height)
    pdf.save('报名凭证.pdf')
  } catch (error) {
    console.error('PDF导出失败:', error)
  }
}

const goToStudentDetail = () => {
  if (props.studentId) {
    router.push('/main/student/' + props.studentId)
  }
}

onMounted(() => {
  fetchOrderData()
})

// orderIds 变化时重新获取数据（关闭弹窗后重新打开不同订单时触发）
watch(() => props.orderIds, () => {
  if (props.orderIds.length > 0) {
    fetchOrderData()
  }
})

defineExpose({
  exportPDF
})
</script>

<style scoped>
.voucher-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.voucher-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
}

/* 针式打印凭证样式 */
.voucher-container {
  width: 210mm;
  padding: 20px;
  margin: 0 auto;
  background: white;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

.voucher-header {
  text-align: center;
  margin-bottom: 20px;
}

.title {
  font-size: 24px;
  font-weight: bold;
}

.voucher-info {
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
}

.order-number {
  font-weight: bold;
}

.voucher-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.voucher-table th,
.voucher-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center;
}

.voucher-table th {
  background-color: #f5f5f5;
}

.voucher-remarks {
  margin: 20px 0;
}

.voucher-summary {
  margin: 20px 0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  font-weight: bold;
}

.voucher-footer {
  margin-top: 40px;
}

.footer-row {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

@media print {
  .voucher-actions,
  .el-dialog__wrapper {
    display: none !important;
  }

  .voucher-container {
    box-shadow: none;
  }

  /* 热敏打印样式 */
  @page {
    margin: 0;
  }

  body {
    margin: 0;
  }

  .thermal-voucher {
    width: 100%;
    color: #000 !important;
  }

  .thermal-voucher * {
    color: #000 !important;
  }
}

/* 热敏打印样式 */
.thermal-voucher {
  width: 58mm;  /* 标准热敏纸宽度 */
  padding: 4mm;  /* 留出边距但不过多占用纸宽 */
  box-sizing: border-box;  /* padding 计入 58mm 内，避免超出纸宽 */
  font-size: 13px;
  line-height: 1.6;
  font-family: "PingFang SC", "Microsoft YaHei", "SimHei", monospace;
  font-weight: 500;
  color: #000 !important;  /* 纯黑，热敏打印头才能充分加热显色 */
  margin: 0 auto;
  background: white;
}

.thermal-voucher .header {
  text-align: center;
  margin-bottom: 10px;
  color: #000;
}

.thermal-voucher .header h1 {
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 5px 0;
  color: #000;
}

.thermal-voucher .divider {
  margin: 8px 0;
  color: #000;
}

.thermal-voucher .content {
  margin: 10px 0;
}

.thermal-voucher .student-info > div {
  margin: 3px 0;
}

/* 祝福语区域 */
.blessing-spacer {
  height: 20px;
}

.blessing {
  text-align: center;
}

.blessing > div {
  margin: 3px 0;
  color: #000;
}

.blessing-deco-top {
  margin-bottom: 8px;
}

.blessing-deco-bottom {
  margin-top: 8px;
  margin-bottom: 12px;
}

.blessing-stars {
  font-size: 11px;
  margin-bottom: 4px;
}

.blessing-tear {
  font-size: 10px;
  color: #555 !important;
}

:deep(.thermal-dialog) {
  .el-dialog__body {
    display: flex;
    justify-content: center;
    background-color: #f5f5f5;
    padding: 20px;
  }
}

.class-info {
  margin: 10px 0;
  padding: 5px 0;
}

.voucher-table td {
  vertical-align: top;
}

.thermal-voucher .class-info + .class-info {
  border-top: 1px dashed #eee;
  margin-top: 10px;
  padding-top: 10px;
}
</style>
